import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
    getVendorWallet,
    topUpVendorWallet,
    deductVendorWallet,
    updateVendorWalletSettings,
    getCityVendorWallet,
    updateCityVendorWallet,
} from "services/vendorWallet";
import { vendorKeys } from "hooks/useVendors";

/** Keyed per restaurant (users.id) and per page of its ledger. */
export const vendorWalletKeys = {
    all: ["vendor-wallet"],
    detail: (userId) => ["vendor-wallet", String(userId)],
    city: (cityId) => ["city-vendor-wallet", String(cityId)],
};

/**
 * One restaurant's wallet and a page of its ledger (api#147).
 *
 * `refreshOnFocus` is for the restaurant's own screens: an order or a refund
 * moves the balance while the tab is in the background, so the figure and the
 * closure reason must be read again when the owner comes back — never inferred
 * from an earlier click.
 *
 * @param {number|string|null} userId Falsy disables the query.
 * @param {number} [page]
 * @param {{refreshOnFocus?: boolean}} [options]
 */
export const useVendorWallet = (userId, page = 1, { refreshOnFocus = false } = {}) => {
    const query = useQuery({
        queryKey: [...vendorWalletKeys.detail(userId), page],
        queryFn: () => getVendorWallet(userId, { page, size: 10 }),
        enabled: Boolean(userId),
        refetchOnWindowFocus: refreshOnFocus,
        // Orders keep arriving while the owner watches; a minute keeps the
        // closure notice honest without polling hard.
        refetchInterval: refreshOnFocus ? 60_000 : false,
        placeholderData: (previous) => previous,
    });

    return {
        vendor: query.data?.vendor ?? null,
        wallet: query.data?.wallet ?? null,
        transactions: query.data?.results ?? [],
        pages: query.data?.pages ?? 1,
        loading: query.isLoading,
        isFetching: query.isFetching,
        error: query.error ? query.error.response?.data?.message || "تعذر تحميل المحفظة" : null,
        refetch: query.refetch,
    };
};

/** Refresh every screen that shows this restaurant's money or its status. */
const invalidateVendorWallet = (queryClient, userId) => {
    queryClient.invalidateQueries({ queryKey: vendorWalletKeys.detail(userId) });
    // The list carries the wallet column and the status the wallet can change.
    queryClient.invalidateQueries({ queryKey: vendorKeys.all });
};

export const useTopUpVendorWallet = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ userId, amount, note, idempotencyKey }) =>
            topUpVendorWallet(userId, { amount, note, idempotencyKey }),
        onSuccess: (_data, variables) => invalidateVendorWallet(queryClient, variables.userId),
    });
};

export const useDeductVendorWallet = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ userId, amount, note, idempotencyKey }) =>
            deductVendorWallet(userId, { amount, note, idempotencyKey }),
        onSuccess: (_data, variables) => invalidateVendorWallet(queryClient, variables.userId),
    });
};

export const useSaveVendorWalletSettings = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ userId, enabled }) => updateVendorWalletSettings(userId, enabled),
        onSuccess: (_data, variables) => invalidateVendorWallet(queryClient, variables.userId),
    });
};

/**
 * One city's restaurant-wallet switch, and the counts that make the warning real.
 *
 * @param {number|string|null} cityId Falsy disables the query.
 */
export const useCityVendorWallet = (cityId) => {
    const query = useQuery({
        queryKey: vendorWalletKeys.city(cityId),
        queryFn: () => getCityVendorWallet(cityId),
        enabled: Boolean(cityId),
        // The counts move as restaurants are topped up; never a stale warning.
        staleTime: 0,
    });

    return {
        policy: query.data ?? null,
        loading: query.isLoading,
        error: query.error?.response?.data?.message ?? null,
    };
};

export const useSaveCityVendorWallet = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: updateCityVendorWallet,
        onSuccess: (data, variables) => {
            queryClient.setQueryData(vendorWalletKeys.city(variables.cityId), data);
            // Turning a city on or off closes restaurants or lifts their blocks.
            queryClient.invalidateQueries({ queryKey: vendorWalletKeys.all });
            queryClient.invalidateQueries({ queryKey: vendorKeys.all });
        },
    });
};
